import React, { useEffect, useRef } from 'react';
import './Drawer.css';

// AS-S2 (P4 / B1): shared slide-in drawer used across the project module.
// Sits BELOW the application top bar (CSS var `--app-top-bar-height`), so
// the drawer header is always visible (fixes the B1 bug that occluded the
// chrome under the top bar in AS-S1).
//
// Props:
//   open                — render or unmount
//   onClose             — invoked on Esc / backdrop click / explicit close
//   width               — "md" (480px) | "lg" (720px) | "xl" (90vw)
//   title, subtitle     — header content (strings or nodes)
//   closeOnBackdrop     — default true; bulk-action sheets etc. may set false
//   closeOnEsc          — default true
//   footer              — optional sticky footer (action buttons)
//   testId              — root data-testid for tests
//   ariaLabel           — accessible label override (defaults to title)

const widthMap = {
  md: 'app-drawer-md',
  lg: 'app-drawer-lg',
  xl: 'app-drawer-xl',
};

const Drawer = ({
  open,
  onClose,
  width = 'md',
  title,
  subtitle,
  closeOnBackdrop = true,
  closeOnEsc = true,
  footer,
  children,
  testId = 'app-drawer',
  ariaLabel,
}) => {
  const panelRef = useRef(null);

  // Esc-to-close. The body listener is mounted only while open.
  useEffect(() => {
    if (!open || !closeOnEsc) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  // Lock body scroll while a drawer is open — prevents the page beneath
  // from scrolling under the panel on touch/scrollwheel.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  if (!open) return null;

  const widthClass = widthMap[width] || widthMap.md;
  const label = typeof ariaLabel === 'string'
    ? ariaLabel
    : (typeof title === 'string' ? title : 'Drawer');

  const handleBackdropClick = (e) => {
    if (!closeOnBackdrop) return;
    // Only react if the backdrop itself was clicked, not bubbled from panel.
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="app-drawer-root"
      data-testid={testId}
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div className="app-drawer-backdrop" />
      <aside
        ref={panelRef}
        className={`app-drawer-panel ${widthClass}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="app-drawer-header">
          <div className="app-drawer-titles">
            {title && <div className="app-drawer-title">{title}</div>}
            {subtitle && <div className="app-drawer-subtitle">{subtitle}</div>}
          </div>
          <button
            type="button"
            className="app-drawer-close"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            data-testid={`${testId}-close`}
            aria-label="Close"
            title="Close"
          >
            ✕
          </button>
        </header>
        <div className="app-drawer-body">{children}</div>
        {footer && <footer className="app-drawer-footer">{footer}</footer>}
      </aside>
    </div>
  );
};

export default Drawer;
